import {type ColumnViewModeModel} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/columnViewMode';
import {type EvalTableFilter} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/CompareEvaluationsPage/ecpState';
import {type EvalResultsSortBy} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';

/**
 * MUI Grid-facing column-visibility model: keyed by grid field.
 * Absent field = visible
 */
export type ColumnVisibilityModel = Record<string, boolean>;

/**
 * Persisted column visibility: group-level and stored as deltas from the panel
 * default. Config size tracks only user overrides
 */
export type ColumnVisibilityDeltas = Record<string, boolean>;

/**
 * Persisted manual column widths, keyed by column group — the input/output/score
 * key without the per-eval call id (see the grid's `useColumnGroupKey`). An
 * absent key uses the auto-fit width, so config tracks only user overrides.
 */
export type ColumnWidthsModel = Record<string, number>;

/** A persisted comparison slot. An omitted step follows the run's latest. */
export type ComparedEvalEntry = {runId: string; step?: number};

export type EvaluationTableRowSize = 'small' | 'medium' | 'large';
export const LEGACY_EVAL_TABLE_HISTORY_TYPE = 'eval-table' as const;
export const CES_EVAL_TABLE_HISTORY_TYPE = 'eval-table-ces' as const;

export type EvalTableType =
  | typeof LEGACY_EVAL_TABLE_HISTORY_TYPE
  | typeof CES_EVAL_TABLE_HISTORY_TYPE;

export const resolveEvalTableType = (
  config: Pick<EvaluationTablesConfig, 'evalTableType'>
): EvalTableType => config.evalTableType ?? LEGACY_EVAL_TABLE_HISTORY_TYPE;

export const evalTableTypeQualifiedKey = (
  historyKey: string,
  evalTableType: EvalTableType
): string => `${evalTableType}:${historyKey}`;

/**
 * Persisted configuration for the workspace `PanelEvaluationTables` panel.
 */
export interface EvaluationTablesConfig {
  evalName: string;
  /** History value type that selects the legacy or CES implementation. */
  evalTableType?: EvalTableType;
  /** A valid pin wins; otherwise the panel follows the newest resolved run. */
  referenceRunId?: string;
  /** Exact membership when set. Absence enables dynamic seeding. */
  comparedEvals?: ComparedEvalEntry[];
  syncStep?: boolean;
  /** Used when `syncStep === true`. Persisted so step picks survive reloads. */
  globalStep?: number;
  /**
   * Per-run step pin used when `syncStep === false`. Map runId -> step.
   */
  runSteps?: Record<string, number>;
  /**
   * Persisted input/output/score filters for the comparison table. Mirrors
   * the URL-persisted filters on CompareEvaluationsPage. When unset, the
   * table opens with no filters.
   */
  evalFilters?: EvalTableFilter[];
  /** Absence enables the default sort; an empty array is explicitly unsorted. */
  sorting?: EvalResultsSortBy[];
  // Persisted column-visibility deltas from the panel default.
  columnVisibility?: ColumnVisibilityDeltas;
  // Persisted manual column widths, keyed by column group. Absent = auto-fit.
  columnWidths?: ColumnWidthsModel;
  /** Persisted table page size. The current page remains transient. */
  pageSize?: number;
  /**
   * Persisted row-size override. When unset, the table derives its size from
   * whether the rendered cells contain tall media.
   */
  rowSize?: EvaluationTableRowSize;
  /** Whether the aggregated-scores section is collapsed. Defaults to false. */
  aggregatedScoresCollapsed?: boolean;
  /**
   * Persisted per-column view-mode (presentation mode) choices, keyed by
   * logical field path (e.g. `output.foo`). Absent field = the cell's default
   * mode, so config size tracks only columns the user explicitly re-typed.
   */
  columnViewModes?: ColumnViewModeModel;
}

/**
 * Shared default for the step syncing mode.
 */
export const DEFAULT_SYNC_STEP = false;

/** Number of slots in the default seed, including the reference. */
export const DEFAULT_MAX_COMPARED_RUNS = 2;
/** Keep the strip readable and the trace query within its 200-call page. */
export const EVAL_TABLE_MAX_COMPARED_RUNS_LIMIT = 10;

/** Registered panel type string. */
export const EVALUATION_TABLES_PANEL_TYPE = 'Evaluation Tables';

/**
 * Max workspace runs the panel resolves into eval calls. Also bounds the number
 * of compared evals (one per run), so the panel never fetches an unbounded run
 * set out of a large workspace.
 */
export const EVAL_TABLE_MAX_RUNS = 100;

/**
 * Path to the attribute on `Evaluation.evaluate` calls that marks them as
 * originating from the wandb SDK's `EvalTable` logger.
 */
export const WANDB_EVAL_TABLE_ATTRIBUTE_PATH =
  'attributes._weave_eval_meta.wandb_eval_table';
